import React, { useEffect } from "react";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { Button, Form, Image, Input, InputNumber, Popconfirm } from "antd";
import TextArea from "antd/es/input/TextArea";

import ButtonWrapper from "../../components/reuseComp/ButtonWrapper";
import { SectionField } from "./HomeTabs";
import UploadImage from "../../components/UploadImage";

interface TrackItems {
  digit: number;
  label: string;
  suffix: string;
}

export interface TrackRecordProps {
  heading?: string;
  subtext?: string;
  track?: TrackItems[];
}

interface Props {
  isEditing: boolean;
  setIsEditing: (v: boolean) => void;
  handleCancel: () => void;
  handleSave: () => void;
  onDelete: (index: number, field: SectionField) => void;
  form: any;
  trackRecord?: TrackRecordProps;
  loading?: boolean;
  mode: "list" | "add" | "edit";
  setMode: (value: "list" | "add" | "edit") => void;
  editingIndex: number | null;
  setEditingIndex: (value: number | null) => void;
}

const TrackRecord: React.FC<Props> = ({
  isEditing,
  setIsEditing,
  handleCancel,
  handleSave,
  form,
  trackRecord,
  onDelete,
  loading,
  mode,
  setMode,
  editingIndex,
  setEditingIndex,
}) => {
  useEffect(() => {
    if (!trackRecord) return;

    if (mode === "list") {
      form.setFieldsValue({
        trackRecord: {
          heading: trackRecord?.heading || "",
          subtext: trackRecord?.subtext || "",
          track:
            trackRecord?.track?.map((item) => ({
              digit: item?.digit,
              label: item?.label,
              suffix: item?.suffix,
            })) || [],
        },
      });
    }

    if (mode === "add") {
      form.setFieldsValue({
        trackRecord: {
          track: [
            {
              label: "",
              digit: undefined,
              suffix: "",
            },
          ],
        },
      });
    }

    if (
      mode === "edit" &&
      editingIndex !== null &&
      trackRecord?.track?.[editingIndex]
    ) {
      const item = trackRecord.track[editingIndex];

      form.setFieldsValue({
        trackRecord: {
          track: [
            {
              label: item?.label,
              digit: item?.digit,
              suffix: item?.suffix,
            },
          ],
        },
      });
    }
  }, [mode, editingIndex, trackRecord, form]);

  return (
    <div>
      {isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl mb-4">
          <Form layout="vertical" form={form}>
            <Form.Item
              label="Heading"
              name={["trackRecord", "heading"]}
              rules={[{ required: true, message: "Enter heading" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Subtext"
              name={["trackRecord", "subtext"]}
              rules={[{ required: true, message: "Enter subtext" }]}
            >
              <TextArea rows={3} />
            </Form.Item>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                setIsEditing(false);
                handleCancel();
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="rounded-[20px] overflow-hidden hidden sm:block">
          <table className="min-w-full">
            <thead>
              <tr className="bg-primary text-white">
                <th className="py-3 px-4 text-left">Heading</th>
                <th className="py-3 px-4 text-left">Subtext</th>
                <th className="py-3 px-4 text-right">Action</th>
              </tr>
            </thead>

            <tbody className="bg-[#F9F9F9]">
              {trackRecord?.heading || trackRecord?.subtext ? (
                <tr className="border-b">
                  <td className="px-4 py-3">{trackRecord?.heading}</td>
                  <td className="px-4 py-3">{trackRecord?.subtext}</td>
                  <td className="px-4 py-3 text-right">
                    <EditOutlined
                      className="text-primary text-xl cursor-pointer"
                      onClick={() => setIsEditing(true)}
                    />
                  </td>
                </tr>
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-6">
                    <Button type="dashed" onClick={() => setIsEditing(true)}>
                      Add Data
                    </Button>
                  </td>
                </tr>
              )}

              <tr>
                <td colSpan={3} className="px-4 py-3 text-right">
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setMode("add");
                      setEditingIndex(null);
                    }}
                    className="text-primary font-semibold"
                  >
                    Add Track Reference
                  </Button>
                </td>
              </tr>

              <tr>
                <td colSpan={3} className="p-4">
                  <p className="font-semibold mb-3">Reference Numbers</p>

                  <table className="min-w-full border border-gray-200 rounded-lg overflow-hidden">
                    <thead>
                      <tr className="bg-gray-100">
                        <th className="px-4 py-2 text-left">Label</th>
                        <th className="px-4 py-2 text-left">Reference</th>
                        <th className="px-4 py-2 text-right">Action</th>
                      </tr>
                    </thead>

                    <tbody>
                      {trackRecord?.track?.length ? (
                        trackRecord.track.map((item, index) => (
                          <tr key={index} className="border-t">
                            <td className="px-4 py-2">{item.label}</td>

                            <td className="px-4 py-2">
                              {item.digit}
                              {item.suffix}
                            </td>

                            <td className="px-4 py-2 text-right">
                              <div className="flex justify-end gap-4 text-lg">
                                <EditOutlined
                                  className="cursor-pointer text-primary"
                                  onClick={() => {
                                    setMode("edit");
                                    setEditingIndex(index);
                                  }}
                                />

                                <Popconfirm
                                  title="Are you sure to delete this?"
                                  onConfirm={() =>
                                    onDelete(index, "trackRecord")
                                  }
                                >
                                  <DeleteOutlined className="cursor-pointer text-red-500" />
                                </Popconfirm>
                              </div>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td
                            colSpan={3}
                            className="px-4 py-6 text-center text-gray-500"
                          >
                            No reference numbers added
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="block sm:hidden mb-4">
          <div className="bg-[#F9F9F9] border border-gray-200 rounded-lg p-4 shadow-sm space-y-4">
            <div>
              <div><b className="text-primary">Heading:</b></div>
              <p>{trackRecord?.heading || "—"}</p>
            </div>
  

            <div>
              <div><b className="text-primary">Subtext:</b></div>
              <p>{trackRecord?.subtext || "—"}</p>
            </div>

            <div>
              <EditOutlined
                onClick={() => setIsEditing(true)}
                className="cursor-pointer text-primary text-[20px]"
              />
            </div>
          </div>

          <Button
            type="dashed"
            className="mt-3 text-primary font-semibold"
            icon={<PlusOutlined />}
            onClick={() => {
              setMode("add");
              setEditingIndex(null);
            }}
            
          >
            Add Areas
          </Button>
        </div>
      )}

      {mode === "list" && !isEditing && (
        <div className="sm:hidden mt-4 space-y-4">
          {trackRecord?.track?.map((item: any, index: any) => (
            <div
              key={index}
              className="bg-[#F9F9F9] border border-gray-200 rounded-lg p-4 shadow-sm"
            >
              <div>
                <div>
                  <p className="font-semibold text-primary">Label:</p> {item?.label}
                </div>
              </div>
              <div>
                <div>
                  <p className="font-semibold text-primary">Reference:</p> {item?.digit} {item?.suffix}
                </div>
              </div>

              <div className="flex justify-end gap-4 text-primary text-base mt-3">
                <EditOutlined
                  className="cursor-pointer"
                  onClick={() => {
                    setMode("edit");
                    setEditingIndex(index);
                  }}
                />
                <Popconfirm
                  title="Are you sure to delete this?"
                  onConfirm={() => onDelete(index, "trackRecord")}
                  okText="Yes"
                  cancelText="No"
                >
                  <DeleteOutlined className="cursor-pointer text-red-500" />
                </Popconfirm>
              </div>
            </div>
          ))}
        </div>
      )}
      {mode !== "list" && !isEditing && (
        <div className="bg-[#F9F9F9] p-5 rounded-xl">
          <Form layout="vertical" form={form}>
            <Form.List name={["trackRecord", "track"]}>
              {(fields) => (
                <>
                  {fields.map((field) => (
                    <div key={field.key} className="border p-4 rounded mb-4">
                      <Form.Item
                        label="Label"
                        name={[field.name, "label"]}
                        rules={[{ required: true, message: "Enter label" }]}
                      >
                        <Input placeholder="enter label..."/>
                      </Form.Item>
                      <div className="grid grid-cols-2 gap-5">
                        <Form.Item
                          label="Reference"
                          name={[field.name, "digit"]}
                          rules={[
                            { required: true, message: "Enter Reference" },
                          ]}
                        >
                          <InputNumber
                            style={{ width: "100%" }}
                            placeholder="Enter number..."
                          />
                        </Form.Item>
                        <Form.Item label="Suffix" name={[field.name, "suffix"]}>
                          <Input placeholder="enter suffix..."/>
                        </Form.Item>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </Form.List>
          </Form>

          <div className="flex gap-3 mt-4">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={loading}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper
              onClick={() => {
                handleCancel();
                setMode("list");
                setEditingIndex(null);
              }}
            >
              Cancel
            </ButtonWrapper>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrackRecord;
